import { useEffect, useState } from 'react';
import { api, type CarKind } from '../lib/api';
import { canScreen, useMe } from '../lib/me';
import { ErrorBox } from './DataTable';
import Modal from './Modal';

/**
 * 차종 고르기(게시판 #171 · 2026-10-12 way 「입찰과 연결되니 설정 기반으로」).
 *
 * 목록 = 차종 설정(`car_kind_option`, 서버 `carKind.ts`) 하나. 노선에는 이름이 그대로 들어간다.
 * - 예전 자유 입력 값(44·77 등)은 목록에 없어도 **그 값 그대로 한 줄** 보여 준다 — 다른 칸만 고쳐 저장해도 안 바뀐다.
 * - 「차종 설정」(배차 입력 권한)에서 목록·허용 정원을 고친다. 허용 정원은 기사앱 「조건차량」 입찰 거르기에 쓰인다.
 * 목록은 세션에 한 번 받고, 설정을 저장하면 다시 받아 열린 고르기 칸이 전부 따라온다.
 */

let cache: Promise<CarKind[]> | null = null;
const listeners = new Set<(rows: CarKind[]) => void>();

function load(force = false): Promise<CarKind[]> {
  if (!cache || force) {
    cache = api.listCarKinds({}).then((r) => r.rows).catch((e) => { cache = null; throw e; });
  }
  return cache;
}

function publish(rows: CarKind[]) {
  cache = Promise.resolve(rows);
  for (const fn of listeners) fn(rows);
}

export function useCarKinds(): CarKind[] | null {
  const [rows, setRows] = useState<CarKind[] | null>(null);
  useEffect(() => {
    let alive = true;
    load().then((r) => { if (alive) setRows(r); }).catch(() => { if (alive) setRows([]); });
    const fn = (r: CarKind[]) => setRows(r);
    listeners.add(fn);
    return () => { alive = false; listeners.delete(fn); };
  }, []);
  return rows;
}

/** 허용 정원 보기 — 「40~47」처럼 이어진 것은 묶는다. */
export function seatsLabel(seats: number[]): string {
  if (!seats.length) return '제한 없음';
  const s = [...seats].sort((a, b) => a - b);
  const out: string[] = [];
  let a = s[0]!;
  let b = a;
  for (const n of s.slice(1)) {
    if (n === b + 1) { b = n; continue; }
    out.push(a === b ? `${a}` : `${a}~${b}`);
    a = b = n;
  }
  out.push(a === b ? `${a}` : `${a}~${b}`);
  return out.join(', ');
}

/** 「40~47, 28」 → [40..47, 28]. 못 읽는 조각은 null(저장 막음). */
export function parseSeats(text: string): number[] | null {
  const out = new Set<number>();
  for (const raw of text.split(',')) {
    const part = raw.trim();
    if (!part) continue;
    const m = part.split('~').map((x) => x.trim());
    if (m.length > 2) return null;
    const lo = Number(m[0]);
    const hi = Number(m[1] ?? m[0]);
    if (!Number.isInteger(lo) || !Number.isInteger(hi) || lo < 1 || hi > 99 || lo > hi) return null;
    for (let n = lo; n <= hi; n++) out.add(n);
  }
  return [...out].sort((x, y) => y - x);
}

export function CarKindSelect({
  value,
  onChange,
  className = 'field',
  showSettings = true,
}: {
  value: string | undefined;
  onChange: (v: string) => void;
  className?: string;
  /** 옆에 「설정」 버튼(배차 입력 권한일 때만 보인다). 표 안 좁은 칸은 끈다. */
  showSettings?: boolean;
}) {
  const rows = useCarKinds();
  const { me } = useMe();
  const canEdit = canScreen(me, 'dispatch');
  const [open, setOpen] = useState(false);
  const usable = (rows ?? []).filter((r) => r.is_use);
  const known = !value || usable.some((r) => r.name === value);

  return (
    <div className="flex items-center gap-1">
      <select className={`${className} min-w-0 flex-1`} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">{rows === null ? '불러오는 중…' : '차종 고르기'}</option>
        {usable.map((r) => (
          <option key={r.car_kind_option_id} value={r.name}>{r.name}</option>
        ))}
        {!known && <option value={value}>{value} (예전 값)</option>}
      </select>
      {showSettings && canEdit && (
        <button type="button" className="btn-ghost shrink-0 px-2 py-1 text-xs" title="차종 목록·허용 정원 고치기" onClick={() => setOpen(true)}>
          설정
        </button>
      )}
      {open && (
        <Modal width="max-w-2xl">
          <CarKindSettings onClose={() => setOpen(false)} />
        </Modal>
      )}
    </div>
  );
}

/**
 * 차량 연식 제한(게시판 #214) — 값은 **차령(년)**: 레거시 `Dispt.CarYear` 그대로(1~10 실측, 0 = 제한 없음).
 * 「조건차량」 입찰에서 그 해 수 이내 차량을 가진 기사에게만 보인다(서버 `driver.ts openBids`).
 * 예전 자유 입력 값이 목록 밖(16년 이상)이면 그 값 그대로 한 줄 보여 준다.
 */
export function CarYearSelect({
  value,
  onChange,
  className = 'field',
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  className?: string;
}) {
  const v = value && value > 0 ? value : 0;
  const year = new Date().getFullYear();
  return (
    <select
      className={className}
      value={v}
      title={v ? `${year - v}년식 이후 차량만 (공개입찰 신청)` : '연식 제한 없음'}
      onChange={(e) => onChange(Number(e.target.value) || undefined)}
    >
      <option value={0}>제한 없음</option>
      {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => (
        <option key={n} value={n}>{n}년 이내</option>
      ))}
      {v > 15 && <option value={v}>{v}년 이내</option>}
    </select>
  );
}

/** 표 머리처럼 고르기 칸 옆에 둘 자리가 없을 때 쓰는 「차종 설정」 버튼(배차 입력 권한일 때만). */
export function CarKindSettingsButton({ className = 'btn-ghost py-1 text-xs' }: { className?: string }) {
  const { me } = useMe();
  const [open, setOpen] = useState(false);
  if (!canScreen(me, 'dispatch')) return null;
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>차종 설정</button>
      {open && (
        <Modal width="max-w-2xl">
          <CarKindSettings onClose={() => setOpen(false)} />
        </Modal>
      )}
    </>
  );
}

interface EditRow { key: number; id: number | null; name: string; seats: string; isUse: boolean }
let editSeq = 0;

/** 차종 설정 창 — 이름·허용 정원·사용, ▲▼ 순서, 지우기. 저장은 한 번에(서버 한 트랜잭션). */
export function CarKindSettings({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<EditRow[] | null>(null);
  const [removed, setRemoved] = useState<{ id: number; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    load(true)
      .then((r) => {
        if (!alive) return;
        setRows(r.map((x) => ({ key: ++editSeq, id: x.car_kind_option_id, name: x.name, seats: seatsLabel(x.seats).replace('제한 없음', ''), isUse: x.is_use })));
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => { alive = false; };
  }, []);

  const setRow = (key: number, patch: Partial<EditRow>) =>
    setRows((cur) => cur && cur.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const move = (i: number, d: -1 | 1) =>
    setRows((cur) => {
      if (!cur) return cur;
      const j = i + d;
      if (j < 0 || j >= cur.length) return cur;
      const next = [...cur];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  const remove = (r: EditRow) => {
    if (r.id) setRemoved((cur) => [...cur, { id: r.id!, name: r.name }]);
    setRows((cur) => cur && cur.filter((x) => x.key !== r.key));
  };

  async function save() {
    if (!rows) return;
    setError(null);
    const out = [];
    for (const [i, r] of rows.entries()) {
      if (!r.name.trim()) return setError(`${i + 1}번째 줄 차종 이름을 넣어 주세요.`);
      const seats = parseSeats(r.seats);
      if (seats === null) return setError(`${i + 1}번째 줄(${r.name}) 허용 정원을 읽을 수 없습니다. 예: 40~47, 28`);
      out.push({ id: r.id, name: r.name.trim(), seats, isUse: r.isUse });
    }
    // 지우기는 접수·노선 고르기 목록에서 바로 사라진다 — 실수로 지운 채 저장하지 않게 한 번 묻는다(#216).
    if (removed.length && !window.confirm(`차종 ${removed.length}개를 목록에서 지웁니다.
${removed.map((r) => r.name).join(', ')}

지우면 접수·노선에서 더 고를 수 없습니다. 저장할까요?`)) return;
    setSaving(true);
    try {
      const res = await api.saveCarKinds({ rows: out, removed: removed.map((r) => r.id) });
      publish(res.rows);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold">차종 설정</h2>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={saving}>취소</button>
        <button className="btn-primary" onClick={save} disabled={saving || !rows}>{saving ? '저장 중…' : '저장'}</button>
      </div>
      <p className="text-xs text-ink-faint">
        접수·노선의 차종은 이 목록에서 고릅니다. <b>허용 정원</b>은 「조건차량」 입찰일 때 그 정원의 차량을 가진 기사님에게만 입찰이 보이게 합니다(비우면 제한 없음).
        이미 그 차종으로 들어간 노선은 이름을 바꿔도 예전 이름 그대로 남습니다.
      </p>
      {error && <ErrorBox message={error} />}
      {!rows ? (
        <div className="text-sm text-ink-soft">불러오는 중…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="w-full min-w-[34rem] text-sm">
            <thead className="bg-canvas text-xs text-ink-soft">
              <tr>
                <th className="w-16 px-2 py-2 text-left font-medium">순서</th>
                <th className="px-2 py-2 text-left font-medium">차종 이름</th>
                <th className="px-2 py-2 text-left font-medium">허용 정원 (예: 40~47, 28)</th>
                <th className="w-14 px-2 py-2 text-center font-medium">사용</th>
                <th className="w-12 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key} className="border-t border-line">
                  <td className="px-2 py-1.5">
                    <button type="button" className="px-1 text-xs text-ink-soft disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)}>▲</button>
                    <button type="button" className="px-1 text-xs text-ink-soft disabled:opacity-30" disabled={i === rows.length - 1} onClick={() => move(i, 1)}>▼</button>
                  </td>
                  <td className="px-2 py-1.5">
                    <input className="field" maxLength={50} value={r.name} onChange={(e) => setRow(r.key, { name: e.target.value })} />
                  </td>
                  <td className="px-2 py-1.5">
                    <input className="field" placeholder="제한 없음" value={r.seats} onChange={(e) => setRow(r.key, { seats: e.target.value })} />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <input type="checkbox" checked={r.isUse} onChange={(e) => setRow(r.key, { isUse: e.target.checked })} />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <button type="button" className="text-xs text-red-600" onClick={() => remove(r)}>지우기</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button
        type="button"
        className="btn-ghost py-1 text-xs"
        disabled={!rows}
        onClick={() => setRows((cur) => [...(cur ?? []), { key: ++editSeq, id: null, name: '', seats: '', isUse: true }])}
      >
        + 차종 추가
      </button>
    </div>
  );
}
